import distance from '@turf/distance';

/** One capture date available around a point, and the picture to open for it. */

export class PanoramaxService {
  /**
   * The federated metacatalog, aggregating every producer (IGN, OSM France…). Individual
   * instances are reachable directly too, but nothing in the app switches to them — the viewer
   * and the map layer both stay on `meta`, same as the reference cartes.app integration.
   */
  static servers = {
    meta: 'https://api.panoramax.xyz',
    ign: 'https://panoramax.ign.fr',
    osm: 'https://panoramax.openstreetmap.fr'
  };
  static baseUrl = PanoramaxService.servers.meta;
  static apiUrl = `${PanoramaxService.baseUrl}/api`;

  /**
   * Vector tiles stop at this zoom, and below it they only carry sequences — the `pictures`
   * source-layer is empty. Further out, a picture has to be looked up through `searchPanorama`.
   */
  static tilesMaxZoom = 15;

  /**
   * How far apart, in metres, two pictures can be and still count as covering the same spot.
   *
   * A street tends to be driven again every few years, and the passes never line up exactly:
   * this has to swallow the GPS spread between them — and the width of a street, since the
   * return pass runs down the other side — without reaching the next picture along the road,
   * which sits 2 to 5 metres away.
   */
  static samePlaceRadius = 15;
  static getTilesUrl() {
    return `${PanoramaxService.apiUrl}/map/{z}/{x}/{y}.mvt`;
  }
  static getThumbnailUrl(pictureId) {
    return `${PanoramaxService.apiUrl}/pictures/${pictureId}/thumb.jpg`;
  }

  /**
   * Finds the 360° picture to open for `center` within `bbox`: the one closest to it, then the
   * most recent of those covering that same spot — the fresh pass is what makes the coverage
   * worth looking at, and the older ones stay reachable from the viewer.
   *
   * `/api/search` silently ignores a `pic_type` filter, but honours a CQL2 `filter` on
   * `field_of_view` (https://gitlab.com/panoramax/server/api/-/work_items/355), so that is what
   * keeps flat pictures out.
   */
  static async searchPanorama(bbox, center, {
    signal
  } = {}) {
    try {
      const params = new URLSearchParams({
        bbox: bbox.join(','),
        limit: '30',
        filter: 'field_of_view=360'
      });
      const candidates = await PanoramaxService.searchCandidates(params, [center.lng, center.lat], {
        signal
      });
      let closest = null;
      for (const candidate of candidates) {
        if (!closest || candidate.distance < closest.distance) closest = candidate;
      }
      if (!closest) return null;
      const freshest = getFreshestAt(candidates, closest);
      return {
        pictureId: freshest.pictureId,
        sequenceId: freshest.sequenceId
      };
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return null;
      console.error('[PanoramaxService][searchPanorama]', err);
      return null;
    }
  }

  /**
   * The capture dates available within `samePlaceRadius` of `center`, most recent first, each
   * with the picture of that date closest to `center`.
   *
   * One entry per date rather than per picture: a single pass leaves a dozen pictures within a
   * few metres, which would make an unreadable list, whereas what the viewer offers to switch
   * between is the passes.
   */
  static async searchNearbyPanoramas(center, {
    signal
  } = {}) {
    try {
      const radius = PanoramaxService.samePlaceRadius;
      // Metres to degrees, generously: the box only narrows the API's answer, `searchCandidates`
      // is what measures the actual distance.
      const latDelta = radius / 111320;
      const lngDelta = latDelta / Math.max(Math.cos(center.lat * Math.PI / 180), 0.01);
      const params = new URLSearchParams({
        bbox: [center.lng - lngDelta, center.lat - latDelta, center.lng + lngDelta, center.lat + latDelta].join(','),
        limit: '100',
        filter: 'field_of_view=360'
      });
      const candidates = await PanoramaxService.searchCandidates(params, [center.lng, center.lat], {
        signal
      });
      const byDate = new Map();
      for (const candidate of candidates) {
        if (candidate.distance > radius) continue;

        // `datetime` is ISO, so the day is its first ten characters.
        const day = candidate.datetime.slice(0, 10);
        const kept = byDate.get(day);
        if (!kept || candidate.distance < kept.distance) byDate.set(day, candidate);
      }
      return [...byDate.values()].sort((a, b) => Date.parse(b.datetime) - Date.parse(a.datetime));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return [];
      console.error('[PanoramaxService][searchNearbyPanoramas]', err);
      return [];
    }
  }

  /** The `/api/search` features that carry everything the two searches above need. */
  static async searchCandidates(params, origin, {
    signal
  } = {}) {
    const response = await fetch(`${PanoramaxService.apiUrl}/search?${params}`, {
      signal
    });
    if (!response.ok) return [];
    const {
      features
    } = await response.json();
    const candidates = [];
    for (const feature of features || []) {
      const coordinates = feature.geometry?.coordinates;
      const datetime = feature.properties?.datetime;

      // A picture with no date cannot be ranked by age nor labelled in the viewer's list.
      if (!feature.id || !coordinates || !datetime) continue;
      candidates.push({
        pictureId: feature.id,
        sequenceId: feature.collection || null,
        datetime,
        coordinates,
        distance: distance(origin, coordinates, {
          units: 'meters'
        })
      });
    }
    return candidates;
  }
  static getViewerUrl([lng, lat], zoom = 17.95) {
    return `${PanoramaxService.baseUrl}/#focus=map&map=${zoom}/${lat}/${lng}`;
  }
  static getPictureUrl(pictureId, [lng, lat], zoom = 17.95) {
    return `${PanoramaxService.baseUrl}/?focus=pic&map=${zoom}/${lat}/${lng}&pic=${pictureId}`;
  }
  static async getLatestPreview({
    bbox,
    from,
    to,
    coordinates
  }) {
    try {
      const params = new URLSearchParams({
        bbox: bbox.join(','),
        datetime: `${from.toISOString()}/${to.toISOString()}`
      });
      const response = await fetch(`${PanoramaxService.baseUrl}/api/search?${params.toString()}`);
      if (!response.ok) return null;
      const data = await response.json();
      const preview = pickRecentPanoramaxPreview(data.features, from, coordinates);
      return preview ? {
        ...preview,
        linkUrl: preview.linkUrl || PanoramaxService.getViewerUrl(preview.coordinates)
      } : null;
    } catch (err) {
      console.error('[PanoramaxService][getLatestPreview]', err);
      throw err;
    }
  }
}

/**
 * The most recent of the candidates covering the same spot as `anchor`, `anchor` itself when
 * nothing fresher is there.
 */
function getFreshestAt(candidates, anchor) {
  let freshest = anchor;
  let freshestTime = Date.parse(anchor.datetime);
  for (const candidate of candidates) {
    const time = Date.parse(candidate.datetime);
    if (time <= freshestTime) continue;
    if (distance(anchor.coordinates, candidate.coordinates, {
      units: 'meters'
    }) > PanoramaxService.samePlaceRadius) continue;
    freshest = candidate;
    freshestTime = time;
  }
  return freshest;
}
function pickRecentPanoramaxPreview(features, cutoff, coordinates) {
  if (!features?.length) return null;
  let best = null;
  for (const feature of features) {
    const datetime = feature.properties?.datetime;
    if (!datetime) continue;
    const date = new Date(datetime);
    if (Number.isNaN(date.valueOf()) || date < cutoff) continue;
    const thumbnailUrl = feature.properties?.['geovisio:thumbnail'] || findThumbnailAsset(feature.assets);
    if (!thumbnailUrl) continue;
    const pictureId = feature.id || feature.properties?.['geovisio:image'];
    if (!best || date > best.date) {
      best = {
        date,
        preview: {
          thumbnailUrl,
          datetime,
          coordinates,
          ...(pictureId ? {
            linkUrl: PanoramaxService.getPictureUrl(pictureId, coordinates)
          } : {})
        }
      };
    }
  }
  return best?.preview || null;
}
function findThumbnailAsset(assets) {
  if (!assets) return null;
  const entries = Object.values(assets);
  const withRole = entries.find(asset => asset.roles?.includes('thumbnail') && asset.href);
  if (withRole?.href) return withRole.href;
  const image = entries.find(asset => asset.type?.startsWith('image/') && asset.href);
  return image?.href || null;
}